/** @file Translate-drag math: axis lock, Shift constrain and grid snap of a dragged world origin. */
import { vec2 } from '@pose2d/math/vec2';
import type { Vec2 } from '@pose2d/math/types';
import { unitAt } from './_internal/unitAt';
import { constrainDelta } from './constrainDelta';
import type { GridView } from './gridTileSize';
import { snapPoint } from './snapPoint';

/** Translate gizmo parts. */
export type TranslatePart = 'free' | 'axisX' | 'axisY';

/** Drag record captured on press: grabbed part, gizmo X-axis world angle, origin and pointer (world). */
export interface TranslateDrag {
  part: TranslatePart;
  rotation: number;
  origin: Vec2;
  start: Vec2;
}

/** Grid plus the resolved modifiers (`dragModifiers`) for one pointer move. */
export interface DragOptions {
  grid: GridView;
  snap: boolean;
  constrain: boolean;
}

/** Restricts delta d in place: onto the grabbed axis, or (free + constrain) onto the dominant grid axis. */
function restrict(drag: TranslateDrag, d: Vec2, opts: DragOptions): Vec2 {
  if (drag.part === 'free') return opts.constrain ? constrainDelta(opts.grid, d[0], d[1], d) : d;
  const axis = unitAt(drag.rotation + (drag.part === 'axisY' ? 90 : 0), [0, 0]);
  return vec2.scale(d, axis, vec2.dot(d, axis));
}

/** New world origin for the pointer at world `pointer`; snapping keeps the axis lock or constraint. */
export function dragTranslate(drag: TranslateDrag, pointer: Vec2, opts: DragOptions, out: Vec2): Vec2 {
  const d = restrict(drag, vec2.sub([0, 0], pointer, drag.start), opts);
  vec2.add(out, drag.origin, d);
  if (!opts.snap || opts.grid.mode === 'off') return out;
  snapPoint(opts.grid, out[0], out[1], out);
  return vec2.add(out, drag.origin, restrict(drag, vec2.sub(d, out, drag.origin), opts));
}
